import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import KeyIcon from '@mui/icons-material/Key';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Alert from '@mui/material/Alert';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Snackbar from '@mui/material/Snackbar';
import { useTheme } from '@mui/material/styles';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { httpsCallable } from 'firebase/functions';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { GradientSpinner, Loading } from '../../core/components/Loading';
import { useAuth } from '../../core/hooks/useAuth';
import { functions } from '../../core/services/firebase';
import { deleteProfileImage, getProfileImageURL, uploadProfileImage } from '../../core/services/storage';
import { decryptWallet, getUserWalletData } from '../../core/services/wallet';

export function ProfilePage() {
  const { user } = useAuth();
  const theme = useTheme();
  const gradient = theme.runport.gradient;
  const { refreshProfile } = useOutletContext();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [nickname, setNickname] = useState('');
  const [walletAddress, setWalletAddress] = useState('');
  const [profileImageURL, setProfileImageURL] = useState(null);
  const [originalProfileImage, setOriginalProfileImage] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [fileExtension, setFileExtension] = useState(null);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('コピーしました');
  const fileInputRef = useRef(null);

  const [walletSalt, setWalletSalt] = useState(null);
  const [walletEncrypted, setWalletEncrypted] = useState(null);
  const [privateKey, setPrivateKey] = useState(null);
  const [showPrivateKey, setShowPrivateKey] = useState(false);
  const [decrypting, setDecrypting] = useState(false);
  const [walletExpanded, setWalletExpanded] = useState(false);

  const fetchProfile = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    try {
      const walletData = await getUserWalletData(user.uid);
      if (walletData) {
        setNickname(walletData.nickname || user.uid.substring(0, 5));
        setWalletAddress(walletData.walletAddress || '');
        setWalletSalt(walletData.salt);
        setWalletEncrypted(walletData.encryptedWallet);
        setOriginalProfileImage(walletData.profileImage);
        if (walletData.profileImage) {
          const url = await getProfileImageURL(walletData.profileImage);
          setProfileImageURL(url);
        }
      }
    } catch (err) {
      console.error('Failed to fetch profile:', err);
      setError('プロフィール情報の取得に失敗しました');
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (user) fetchProfile();
  }, [user, fetchProfile]);

  const handleWalletExpand = async (_event, expanded) => {
    setWalletExpanded(expanded);
    if (expanded && !privateKey && walletSalt && walletEncrypted) {
      setDecrypting(true);
      try {
        const { privateKey: pk } = await decryptWallet(walletSalt, walletEncrypted);
        setPrivateKey(pk);
      } catch (err) {
        console.error('Failed to decrypt wallet:', err);
        setError('秘密鍵の復号に失敗しました');
      } finally {
        setDecrypting(false);
      }
    }
  };

  const copyToClipboard = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      setSnackbarMessage(`${label}をコピーしました`);
      setSnackbarOpen(true);
    } catch {
      setSnackbarMessage('コピーに失敗しました');
      setSnackbarOpen(true);
    }
  };

  const handleImageSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setFileExtension(file.type.split('/')[1]);
    setSelectedFile(file);
    const reader = new FileReader();
    reader.onload = (event) => setProfileImageURL(event.target.result);
    reader.readAsDataURL(file);
  };

  const handleSave = async () => {
    if (!nickname.trim()) {
      setError('ニックネームを入力してください');
      return;
    }
    setSaving(true);
    setError(null);
    setSuccess(false);

    try {
      const updateUserProfileFn = httpsCallable(functions, 'updateUserProfile');

      if (selectedFile) {
        const blob = await fetch(profileImageURL).then((r) => r.blob());
        const uploadTask = uploadProfileImage(user.uid, blob, fileExtension);
        await new Promise((resolve, reject) => {
          uploadTask.on('state_changed', null, reject, resolve);
        });

        await updateUserProfileFn({
          nickname,
          profileImage: `${user.uid}.${fileExtension}`,
        });

        if (originalProfileImage && originalProfileImage !== `${user.uid}.${fileExtension}`) {
          await deleteProfileImage(originalProfileImage).catch(() => {});
        }
        setOriginalProfileImage(`${user.uid}.${fileExtension}`);
      } else {
        await updateUserProfileFn({ nickname });
      }
      setSelectedFile(null);
      setSuccess(true);
      await refreshProfile();
    } catch (err) {
      console.error('Failed to save profile:', err);
      setError('保存に失敗しました');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Loading />;

  return (
    <Box>
      <Typography
        component="h1"
        sx={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0 0 0 0)' }}
      >
        プロフィール
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(false)}>
          保存しました
        </Alert>
      )}

      {/* プロフィール画像 */}
      <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
        <Box sx={{ position: 'relative' }}>
          <Box
            sx={{
              width: 108,
              height: 108,
              borderRadius: '50%',
              background: gradient,
              p: '3px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Avatar
              src={profileImageURL}
              sx={{
                width: 102,
                height: 102,
                border: '3px solid',
                borderColor: 'background.paper',
              }}
            />
          </Box>
          <IconButton
            onClick={() => fileInputRef.current?.click()}
            sx={{
              position: 'absolute',
              bottom: 0,
              right: 0,
              background: gradient,
              color: 'white',
              '&:hover': { background: gradient, filter: 'brightness(0.9)' },
              width: 34,
              height: 34,
              boxShadow: '0 2px 8px rgba(255, 56, 92, 0.3)',
            }}
          >
            <PhotoCameraIcon sx={{ fontSize: 16 }} />
          </IconButton>
          <input ref={fileInputRef} type="file" accept="image/*" hidden onChange={handleImageSelect} />
        </Box>
      </Box>

      {/* ニックネーム */}
      <TextField
        fullWidth
        label="ニックネーム"
        value={nickname}
        onChange={(e) => setNickname(e.target.value)}
        sx={{ mb: 3 }}
        disabled={saving}
      />

      {/* 保存ボタン */}
      <Button
        fullWidth
        variant="contained"
        onClick={handleSave}
        disabled={saving}
        sx={{
          py: 1.5,
          mb: 3,
          background: gradient,
          '&:hover': { background: gradient, filter: 'brightness(0.9)' },
        }}
      >
        {saving ? <CircularProgress size={24} color="inherit" /> : '保存'}
      </Button>

      {/* ウォレットセクション */}
      <Accordion
        expanded={walletExpanded}
        onChange={handleWalletExpand}
        sx={{
          bgcolor: 'transparent',
          backgroundImage: 'none',
          '&::before': { display: 'none' },
          border: '1px solid rgba(255,255,255,0.08)',
          borderRadius: '8px !important',
        }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: 'text.secondary' }} />} sx={{ px: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AccountBalanceWalletIcon sx={{ color: 'primary.main', fontSize: 20 }} />
            <Typography variant="subtitle2" fontWeight="bold">
              ウォレット
            </Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={{ px: 2, pt: 0 }}>
          {/* ウォレットアドレス */}
          <Box sx={{ mb: 2 }}>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
              アドレス
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <TextField
                fullWidth
                value={walletAddress}
                slotProps={{ input: { readOnly: true } }}
                size="small"
                sx={{ '& .MuiOutlinedInput-root': { fontSize: '0.85rem', fontFamily: 'monospace' } }}
              />
              <IconButton
                onClick={() => copyToClipboard(walletAddress, 'アドレス')}
                disabled={!walletAddress}
                sx={{ color: 'primary.main' }}
              >
                <ContentCopyIcon />
              </IconButton>
            </Box>
          </Box>

          <Divider sx={{ my: 2 }} />

          {/* 秘密鍵 */}
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
              <KeyIcon sx={{ color: 'primary.main', fontSize: 18 }} />
              <Typography variant="caption" color="text.secondary">
                秘密鍵
              </Typography>
            </Box>

            {decrypting ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1 }}>
                <GradientSpinner size={20} />
                <Typography variant="body2" color="text.secondary">
                  復号化中...
                </Typography>
              </Box>
            ) : (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <TextField
                  fullWidth
                  type={showPrivateKey ? 'text' : 'password'}
                  value={privateKey || ''}
                  slotProps={{ input: { readOnly: true } }}
                  size="small"
                  sx={{ '& .MuiOutlinedInput-root': { fontSize: '0.85rem', fontFamily: 'monospace' } }}
                />
                <IconButton
                  onClick={() => setShowPrivateKey(!showPrivateKey)}
                  disabled={!privateKey}
                  sx={{ color: 'primary.main' }}
                >
                  {showPrivateKey ? <VisibilityOffIcon /> : <VisibilityIcon />}
                </IconButton>
                <IconButton
                  onClick={() => privateKey && copyToClipboard(privateKey, '秘密鍵')}
                  disabled={!privateKey}
                  sx={{ color: 'primary.main' }}
                >
                  <ContentCopyIcon />
                </IconButton>
              </Box>
            )}

            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
              秘密鍵は他人に教えないでください。
            </Typography>
          </Box>
        </AccordionDetails>
      </Accordion>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={2000}
        onClose={() => setSnackbarOpen(false)}
        message={snackbarMessage}
      />
    </Box>
  );
}
